import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
// Hooks
import useShowPassword from 'hooks/useShowPassword';
// Components
import TooltipWithIcon from 'components/TooltipWithIcon';
// Constants
import { PASSWORD_REGEX } from 'constants/constants';
// Styles
import { Alert, TextField, Form } from 'pages/auth/styled';

const NewPasswordFromResetSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'components:SignUp.password_min_length')
    .max(255)
    .matches(PASSWORD_REGEX, 'components:SignUp.password_complexity')
    .required('common:password_required'),
  confirmPassword: Yup.string().when('password', {
    is: (val: string) => val && val.length > 0,
    then: Yup.string().oneOf([Yup.ref('password')], 'components:SignUp.password_same')
  })
});

/**
 * @name ResetPassword
 * @description This form allows the user to choose a new password after clicking on forgot password.
 */
function NewPasswordFromReset({
  handleOnSubmit
}: {
  handleOnSubmit: (values: { password: string; confirmPassword: string }) => void;
}) {
  // We do not specify namespace and keyPrefix, as we need also to translate schema. It does not work otherwise.
  const { t } = useTranslation();
  //show hide password
  const { showPw, setShowPw, passwordInputType } = useShowPassword();
  //form
  const { handleSubmit, errors, values, touched, handleBlur, handleChange, isSubmitting } =
    useFormik({
      initialValues: {
        password: '',
        confirmPassword: '',
        submit: null
      },
      validationSchema: NewPasswordFromResetSchema,
      onSubmit: handleOnSubmit
    });

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {errors.submit && (
        <Alert mt={2} mb={1} severity="warning">
          {errors.submit}
        </Alert>
      )}
      <Grid container>
        <Grid item xs={12} my={touched.password && errors.password ? 8 : 4}>
          <TextField
            inputProps={{
              'data-testid': 'password-input'
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <TooltipWithIcon
                    title={t('components:SignUp.password_complexity')}
                    size="small"
                  />
                </InputAdornment>
              )
            }}
            data-testid="password"
            type={passwordInputType}
            name="password"
            label={t('common:password')}
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={
              touched.password &&
              errors.password && <span data-testid="password-errors">{t(errors.password)}</span>
            }
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} my={5}>
          <TextField
            data-testid="confirm-password"
            inputProps={{
              'data-testid': 'confirm-password-input'
            }}
            type={passwordInputType}
            name="confirmPassword"
            label={t('common:confirm_password')}
            value={values.confirmPassword}
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            fullWidth
            helperText={touched.confirmPassword && t(errors.confirmPassword || '')}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} my={5}>
          <FormControlLabel
            control={
              <Checkbox checked={showPw} onChange={(event) => setShowPw(event.target.checked)} />
            }
            label={t('components:SignUp.show_password')}
          />
        </Grid>
        <Grid item xs={12} my={2}>
          <Button
            data-testid="submit-button"
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {t('common:reset_password')}
          </Button>
        </Grid>
      </Grid>
    </Form>
  );
}

export default NewPasswordFromReset;
